"use client";

import { useSyncExternalStore } from "react";
import { EMPTY_PLAN, PLAN_EVENT, PLAN_KEY, savePlan, type PlannerState } from "@/lib/route-shared";

type Bucket = { raw: string | null; plan: PlannerState };
const buckets = new Map<string, Bucket>();

function bucket(key: string) {
  let b = buckets.get(key);
  if (!b) {
    b = { raw: null, plan: EMPTY_PLAN };
    buckets.set(key, b);
  }
  return b;
}

function read(key: string): PlannerState {
  const b = bucket(key);
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    // Speicher gesperrt: Planung gilt nur bis zum Neuladen.
  }
  if (raw !== b.raw) {
    b.raw = raw;
    try {
      const p = JSON.parse(raw ?? "null") as Partial<PlannerState> | null;
      b.plan = p && Array.isArray(p.stops) ? { ...EMPTY_PLAN, ...p, stops: p.stops.filter((s) => Number.isInteger(s?.id)) } : EMPTY_PLAN;
    } catch {
      b.plan = EMPTY_PLAN;
    }
  }
  return b.plan;
}

function subscribe(cb: () => void) {
  window.addEventListener(PLAN_EVENT, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(PLAN_EVENT, cb);
    window.removeEventListener("storage", cb);
  };
}

/** Planung aus localStorage; null beim Server-Rendern. Jeder Schlüssel hat seinen eigenen Cache. */
export const usePlan = (key = PLAN_KEY) => useSyncExternalStore(subscribe, () => read(key), () => null);

export function updatePlan(patch: Partial<PlannerState>, key = PLAN_KEY) {
  savePlan({ ...read(key), ...patch }, key);
}

/** Stopp hinzufügen oder entfernen; false, wenn das Limit erreicht ist. */
export function toggleStop(id: number, max: number, key = PLAN_KEY) {
  const p = read(key);
  if (p.stops.some((s) => s.id === id)) {
    updatePlan({ stops: p.stops.filter((s) => s.id !== id) }, key);
    return true;
  }
  if (p.stops.length >= max) return false;
  updatePlan({ stops: [...p.stops, { id }] }, key);
  return true;
}
